import React from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const messages = defineMessages({
	currentValue: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.rich-text-field.current-value',
		defaultMessage: 'Current {name}',
		description: 'Label for the current value in the rich text field',
	},
	suggestedValue: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.rich-text-field.suggested-value',
		defaultMessage: 'Suggested update',
		description: 'Label for the suggested value update in the rich text field',
	},
});

const styles = cssMap({
	container: {
		paddingInline: token('space.300'),
	},
	withTopSpace: {
		paddingTop: token('space.200'),
	},
	withBorder: {
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
	},
});

export type TooltipContentSection = React.ReactNode | string | number | boolean | null | undefined;

type AdditionalSuggestions = {
	suggested?: TooltipContentSection;
	label?: string;
	maxLines?: number;
	testIdSuffix?: string;
};

export type Props = {
	reasoning?: TooltipContentSection;
	current?: TooltipContentSection;
	suggested?: TooltipContentSection;
	fieldName?: string;
	currentLabel?: string;
	currentMaxLines?: number;
	suggestedLabel?: string;
	suggestedMaxLines?: number;
	additionalSuggestions?: AdditionalSuggestions[];
};

export const FieldTooltipContent = ({
	reasoning,
	current,
	suggested,
	fieldName,
	currentLabel,
	currentMaxLines = 5,
	suggestedLabel,
	suggestedMaxLines = 10,
	additionalSuggestions = [],
}: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	if (!reasoning && !current && !suggested) {
		return null;
	}

	return (
		<Stack
			testId="update-work-item-fields-action-renderer.ui.action-card.card-fields-list.field-tooltip-content"
			space="space.200"
		>
			{reasoning && (
				<Box
					xcss={styles.container}
					testId="update-work-item-fields-action-renderer.ui.action-card.card-fields-list.field-tooltip-content.reasoning"
				>
					{typeof reasoning === 'string' ? <Text>{reasoning}</Text> : reasoning}
				</Box>
			)}
			{current && (
				<Box
					xcss={cx(styles.container, !!reasoning && styles.withTopSpace)}
					testId="update-work-item-fields-action-renderer.ui.action-card.card-fields-list.field-tooltip-content.current"
				>
					<Stack space="space.100">
						<Heading size="xsmall">
							{currentLabel
								? currentLabel
								: formatMessage(messages.currentValue, { name: fieldName?.toLowerCase() ?? '' })}
						</Heading>
						{typeof current === 'string' ? (
							<Text maxLines={currentMaxLines}>{current}</Text>
						) : (
							current
						)}
					</Stack>
				</Box>
			)}
			{suggested && (
				<Box
					xcss={cx(
						styles.container,
						(!!current || !!reasoning) && styles.withTopSpace,
						(!!current || !!reasoning) && styles.withBorder,
					)}
					testId="update-work-item-fields-action-renderer.ui.action-card.card-fields-list.field-tooltip-content.suggested"
				>
					<Stack space="space.100">
						<Heading size="xsmall">
							{suggestedLabel ? suggestedLabel : formatMessage(messages.suggestedValue)}
						</Heading>
						{typeof suggested === 'string' ? (
							<Text maxLines={suggestedMaxLines}>{suggested}</Text>
						) : (
							suggested
						)}
					</Stack>
				</Box>
			)}
			{additionalSuggestions.map(
				({ suggested, label, maxLines = 10, testIdSuffix }) =>
					suggested && (
						<Box
							xcss={cx(
								styles.container,
								(!!current || !!suggested || !!reasoning) && styles.withTopSpace,
								(!!current || !!suggested || !!reasoning) && styles.withBorder,
							)}
							testId={getAdditionalTestId(testIdSuffix)}
						>
							<Stack space="space.100">
								<Heading size="xsmall">
									{label ? label : formatMessage(messages.suggestedValue)}
								</Heading>
								{typeof suggested === 'string' ? (
									<Text maxLines={maxLines}>{suggested}</Text>
								) : (
									suggested
								)}
							</Stack>
						</Box>
					),
			)}
		</Stack>
	);
};

const getAdditionalTestId = (testIdSuffix = '') => {
	const suffix = testIdSuffix ? `.${testIdSuffix}` : '';
	return `update-work-item-fields-action-renderer.ui.action-card.card-fields-list.field-tooltip-content.additional-suggestion${suffix}`;
};
